import { NavLink, Outlet } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  BarChart3,
  BookOpen,
  CreditCard,
  LineChart,
  LogOut,
  Mail,
  Plug,
  Settings as SettingsIcon,
  Hexagon,
  Brain,
} from 'lucide-react'
import { StatusPill } from '@/components/StatusPill'
import { SubscriptionGuard } from '@/components/SubscriptionGuard'
import { EnrichmentGate } from '@/components/EnrichmentGate'
import { PersonaSwitcher } from '@/components/PersonaSwitcher'
import { WakeWord } from '@/components/WakeWord'
import { usePersona } from '@/lib/personas'
import { HAS_AUTH } from '@/lib/auth'
import { useCurrentUser } from '@/lib/hooks'
import { apiClient, unwrap } from '@/lib/apiClient'
import { cn } from '@/lib/cn'
import type { EmailDraftListItem } from '@/types'

interface NavItem {
  to: string
  label: string
  icon: React.ReactNode
  end?: boolean
  /** Optional badge count shown as a small dot over the icon. */
  badge?: number
}

const STATIC_NAV: NavItem[] = [
  { to: '/dashboard', label: 'Command', icon: <Hexagon size={18} />, end: true },
  { to: '/ads', label: 'Ads', icon: <BarChart3 size={18} /> },
  { to: '/email', label: 'Email', icon: <Mail size={18} /> },
  { to: '/metrics', label: 'Metrics', icon: <LineChart size={18} /> },
  { to: '/activity', label: 'Activity', icon: <Activity size={18} /> },
  { to: '/setup', label: 'Setup', icon: <Plug size={18} /> },
  { to: '/guide', label: 'Guide', icon: <BookOpen size={18} /> },
  { to: '/billing', label: 'Billing', icon: <CreditCard size={18} /> },
  { to: '/profile', label: 'Memory', icon: <Brain size={18} /> },
  { to: '/settings', label: 'Settings', icon: <SettingsIcon size={18} /> },
]

function useEmailBadge(): number {
  // Lightweight poll for the count of pending drafts/flags. Background
  // refresh keeps the badge fresh; cheap because the list endpoint is
  // small (Phase 1 cap = 15 messages per sync).
  const q = useQuery<{ items: EmailDraftListItem[] }>({
    queryKey: ['email-drafts'],
    queryFn: () =>
      unwrap<{ items: EmailDraftListItem[] }>(apiClient.get('/email/drafts')),
    enabled: HAS_AUTH,
    refetchInterval: 60_000,
    retry: false,
  })
  return q.data?.items.length ?? 0
}

export function AuthenticatedLayout() {
  const { instance, accounts } = useMsal()
  const { persona } = usePersona()
  const me = useCurrentUser()
  const emailBadge = useEmailBadge()

  const NAV: NavItem[] = STATIC_NAV.map((item) =>
    item.to === '/email' ? { ...item, badge: emailBadge } : item,
  )

  const handleLogout = () => {
    if (!HAS_AUTH) {
      // Dev mock mode — nothing to log out of; redirect to /login for parity.
      window.location.href = '/login'
      return
    }
    instance.logoutRedirect().catch(() => undefined)
  }

  const displayName =
    me.data?.email ?? accounts[0]?.username ?? accounts[0]?.name ?? (HAS_AUTH ? '' : 'dev mode')
  const userInitial =
    (me.data?.name?.[0] ?? accounts[0]?.name?.[0] ?? accounts[0]?.username?.[0] ?? '?')
      .toUpperCase()

  return (
    <div className="flex h-full bg-jarvis-dark text-jarvis-text">
      {/* Sidebar */}
      <aside className="w-16 flex flex-col items-center border-r border-jarvis-border bg-jarvis-surface-2/50">
        <div className="py-4 mb-2">
          <PersonaSwitcher variant="icon" alignment="left" />
        </div>
        <nav className="flex flex-col gap-1 flex-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'relative w-10 h-10 flex items-center justify-center rounded-lg transition-colors',
                  isActive
                    ? 'bg-jarvis-blue/15 text-jarvis-blue'
                    : 'text-jarvis-muted hover:text-jarvis-text hover:bg-jarvis-surface',
                )
              }
              title={item.badge ? `${item.label} (${item.badge})` : item.label}
            >
              {item.icon}
              {item.badge && item.badge > 0 ? (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-jarvis-blue text-[10px] font-semibold text-jarvis-dark flex items-center justify-center">
                  {item.badge > 9 ? '9+' : item.badge}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
        <button
          onClick={handleLogout}
          className="w-10 h-10 mb-4 flex items-center justify-center rounded-lg text-jarvis-muted hover:text-jarvis-red hover:bg-jarvis-surface transition-colors"
          title="Sign out"
        >
          <LogOut size={18} />
        </button>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="flex items-center justify-between px-6 h-14 border-b border-jarvis-border bg-jarvis-surface-2/30">
          <div className="flex items-center gap-3">
            <span className="text-jarvis-blue font-semibold tracking-widest text-sm">
              {persona.wordmark}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <WakeWord />
            <StatusPill status="idle" />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-jarvis-muted truncate max-w-[220px]">
              {displayName || 'Loading…'}
            </span>
            <div className="w-8 h-8 rounded-full bg-jarvis-blue/20 text-jarvis-blue flex items-center justify-center text-sm font-semibold">
              {userInitial}
            </div>
          </div>
        </header>

        {/* Main */}
        <main className="flex-1 overflow-auto">
          <SubscriptionGuard>
            <EnrichmentGate>
              <div className="p-6 max-w-[1400px] mx-auto">
                <Outlet />
              </div>
            </EnrichmentGate>
          </SubscriptionGuard>
        </main>
      </div>
    </div>
  )
}
